import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Recycle,
  MapPin,
  Layers,
  Boxes,
  Sparkles,
} from "lucide-react";

/**
 * 4 typologies de cartons mises en avant — cross-sell vers fiches produit.
 */
const CARTON_TYPES = [
  {
    title: "Carton « Livres & Documents »",
    body: "Petit format mais structure renforcée. Idéal pour les charges lourdes et denses, il évite de se bloquer le dos et empêche le fond de céder.",
    to: "/produit/carton-a-livres",
    label: "Voir le carton livres",
    Icon: Boxes,
  },
  {
    title: "Carton « Standard & Volumes »",
    body: "Le couteau suisse du déménagement. Parfait pour le linge, le petit électroménager et les objets du quotidien.",
    to: "/produit/carton-standard-renforce",
    label: "Voir le carton standard",
    Icon: Package,
  },
  {
    title: "Carton « Penderie »",
    body: "Une véritable armoire mobile en carton avec tringle intégrée. Vos vêtements sur cintres passent directement du placard au camion sans se froisser.",
    to: "/produit/carton-penderie-grand",
    label: "Voir le carton penderie",
    Icon: Layers,
  },
  {
    title: "Cartons « Spécial Vaisselle »",
    body: "Équipés de croisillons protecteurs, ils isolent chaque verre et chaque assiette pour éliminer les risques de friction et de chocs.",
    to: "/produit/carton-75-verres",
    label: "Voir le carton vaisselle",
    Icon: Sparkles,
  },
];
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Charte de transport (3 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Charte de transport
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos fournitures livrées à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne cherchez plus comment transporter des piles de cartons
            volumineux dans votre véhicule. Nous gérons l&apos;acheminement
            de vos commandes de A à Z.
          </p>
          <p className="mt-2 text-[13px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
            Notre charte de transport en Île-de-France
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Zéro intermédiaire",
              body: `Nous n'utilisons aucun sous-traitant. C'est notre propre société et nos chauffeurs qui viennent à votre rencontre à ${city.name}.`,
              Icon: Truck,
            },
            {
              title: "Sécurité totale",
              body: "En maîtrisant notre propre flotte, nous veillons à ce que votre matériel arrive propre, sec et en parfait état.",
              Icon: ShieldCheck,
            },
            {
              title: "Flexibilité régionale",
              body: "Une couverture logistique ciblée uniquement sur l'Île-de-France pour garantir une réactivité maximale.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — L'art de l'emballage ciblé (4 typologies cliquables) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Emballage ciblé
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            À chaque objet sa solution : l&apos;art de l&apos;emballage ciblé
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Emballer un livre comme on emballe une assiette ou un vêtement
            est la meilleure option pour commettre des erreurs. Pour
            garantir un transfert sans casse, notre gamme segmente les
            usages.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {CARTON_TYPES.map(({ title, body, to, label, Icon }) => (
            <Link
              key={title}
              to={to}
              className="block bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 p-5 md:p-6 group"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] group-hover:gap-2.5 transition-all">
                {label}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-bourg-la-reine-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nos fournitures (3 engagements bicolores) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi faire le choix de nos fournitures&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Opter pour nos emballages, c&apos;est refuser les compromis sur
            la sécurité de votre patrimoine. Nous faisons la différence sur
            trois critères essentiels.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Une résistance mécanique supérieure",
              body: "Contrairement aux emballages standards du commerce, nos cartons possèdent un grammage renforcé. Ils supportent l'empilement dans le camion de déménagement sans s'écraser ni déformer vos affaires.",
              Icon: ShieldCheck,
              accent: "#237cb5",
            },
            {
              title: "Une démarche éco-responsable concrète",
              body: "Allier efficacité et respect de l'environnement est possible. Notre matériel est fabriqué à partir de composants 100 % recyclables et biodégradables, réduisant l'impact écologique de votre changement de domicile.",
              Icon: Recycle,
              accent: "#16A34A",
            },
            {
              title: "Un service de proximité maîtrisé",
              body: "En contrôlant nous-mêmes notre chaîne de livraison en Île-de-France, vous bénéficiez d'un suivi fiable, d'horaires respectés et de produits livrés dans un état impeccable, prêts à être montés.",
              Icon: Truck,
              accent: "#237cb5",
            },
          ].map(({ title, body, Icon, accent }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="flex items-center justify-center h-10 w-10 rounded-full text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-bourg-la-reine-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
